/**
 * DebugPanel — bottom-docked diagnostic surface for SuperAdmin / Test Mode.
 *
 * Architecture:
 *   - Visibility gate is computed by `useDebugPanelVisible()` (parent decides
 *     whether to render this component at all). When NOT visible, this file
 *     is never imported because ShellLayout short-circuits before requiring
 *     the export.
 *   - Mounting installs the API + error capture services (idempotent). They
 *     attach at module load via `installApiCallLog()` / `installErrorBuffer()`.
 *   - Header is always visible; body lazy-mounts only when expanded so the
 *     panel has zero render cost in the collapsed steady state beyond the
 *     32px bar.
 *   - All sections are rendered top-to-bottom; each has its own collapse/
 *     expand state persisted to localStorage.
 *
 * Bill 2026-04-24: "Don't skimp." Each section delivers a useful default view
 * even if no module has registered extra state via window.__PCB_DEBUG_EXTRA__.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { redactDebugPayload } from './utils/redactDebugPayload';
import { DebugPanelHeader } from './DebugPanelHeader';
import { useApiCallLog } from './hooks/useApiCallLog';
import { useDebugPanelExpanded } from './hooks/useDebugPanelExpanded';
import { useDebugSnapshot } from './hooks/useDebugSnapshot';
import { useErrorBuffer } from './hooks/useErrorBuffer';
import { installApiCallLog } from './services/apiCallLogStore';
import { installErrorBuffer } from './services/errorBufferStore';

import { IdentitySection } from './sections/IdentitySection';
import { CartStateSection } from './sections/CartStateSection';
import { DialogStateSection } from './sections/DialogStateSection';
import { ApiCallLogSection } from './sections/ApiCallLogSection';
import { TwainStateSection } from './sections/TwainStateSection';
import { ConfigSnapshotSection } from './sections/ConfigSnapshotSection';
import { RoutingSection } from './sections/RoutingSection';
import { TestModeSection } from './sections/TestModeSection';
import { ErrorsSection } from './sections/ErrorsSection';
import { PerformanceSection } from './sections/PerformanceSection';
import { EnvironmentSection } from './sections/EnvironmentSection';
import { FeatureFlagsSection } from './sections/FeatureFlagsSection';

import './DebugPanel.css';

// Install at module load so we capture calls + errors as early as possible
// (before the panel is even opened). Idempotent — repeated calls no-op.
installApiCallLog();
installErrorBuffer();

const AUTO_REFRESH_KEY = 'pcb.debug.panel.autorefresh';

function safeFilename(upn: string): string {
  return (upn || 'unknown').replace(/[^a-zA-Z0-9._-]+/g, '_');
}

export function DebugPanel(): JSX.Element {
  const { expanded, toggle } = useDebugPanelExpanded();
  const { user } = useAuth();
  const apiEntries = useApiCallLog();
  const errorEntries = useErrorBuffer();
  const { build } = useDebugSnapshot();

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [flash, setFlash] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try { return window.localStorage.getItem(AUTO_REFRESH_KEY) === '1'; } catch { return false; }
  });
  const [, setTick] = useState<number>(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try { window.localStorage.setItem(AUTO_REFRESH_KEY, autoRefresh ? '1' : '0'); } catch { /* ignore */ }
  }, [autoRefresh]);

  // Auto-refresh tick: re-render the panel every 2s when ON (and expanded).
  useEffect(() => {
    if (!autoRefresh || !expanded) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 2000);
    return () => window.clearInterval(id);
  }, [autoRefresh, expanded]);

  const errorCount = useMemo(() => errorEntries.filter((e) => e.level === 'error').length, [errorEntries]);
  const apiCount = apiEntries.length;

  const flashFor = useCallback((msg: string) => {
    setFlash(msg);
    window.setTimeout(() => setFlash(null), 1800);
  }, []);

  const onCopy = useCallback(async () => {
    try {
      const snap = build();
      const redacted = redactDebugPayload(snap);
      const text = JSON.stringify(redacted, null, 2);
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback: temporary textarea + execCommand (works in older Teams iframes).
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } catch { /* noop */ }
        document.body.removeChild(ta);
      }
      flashFor('Copied');
    } catch (err) {
      console.error('[DebugPanel] copy failed', err);
      flashFor('Copy failed');
    }
  }, [build, flashFor]);

  const onExport = useCallback(() => {
    try {
      const snap = build();
      const redacted = redactDebugPayload(snap);
      const text = JSON.stringify(redacted, null, 2);
      const blob = new Blob([text], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      a.href = url;
      a.download = `pcb-retail-debug-${safeFilename(user?.upn ?? 'unknown')}-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 0);
      flashFor('Exported');
    } catch (err) {
      console.error('[DebugPanel] export failed', err);
      flashFor('Export failed');
    }
  }, [build, user, flashFor]);

  return (
    <div
      className={`pcb-debug-panel ${expanded ? 'is-expanded' : 'is-collapsed'}`}
      role="complementary"
      aria-label="Diagnostic Debug Panel"
    >
      <DebugPanelHeader
        expanded={expanded}
        onToggleExpanded={toggle}
        errorCount={errorCount}
        apiCount={apiCount}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        autoRefresh={autoRefresh}
        onToggleAutoRefresh={() => setAutoRefresh((v) => !v)}
        onCopy={onCopy}
        onExport={onExport}
        flash={flash}
      />
      {expanded && (
        <div className="pcb-debug-panel__body">
          <IdentitySection searchTerm={searchTerm} />
          <CartStateSection searchTerm={searchTerm} />
          <DialogStateSection searchTerm={searchTerm} />
          <ApiCallLogSection searchTerm={searchTerm} />
          <TwainStateSection searchTerm={searchTerm} />
          <ConfigSnapshotSection searchTerm={searchTerm} />
          <RoutingSection searchTerm={searchTerm} />
          <TestModeSection searchTerm={searchTerm} />
          <ErrorsSection searchTerm={searchTerm} />
          <PerformanceSection searchTerm={searchTerm} />
          <EnvironmentSection searchTerm={searchTerm} />
          <FeatureFlagsSection searchTerm={searchTerm} />
        </div>
      )}
    </div>
  );
}
